import { useEffect, useState } from "react";
import axios from "axios";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CarCard from "@/components/CarCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cars = () => {
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [marqueFilter, setMarqueFilter] = useState("all");
  const [carburantFilter, setCarburantFilter] = useState("all");
  const [priceRange, setPriceRange] = useState("all");
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await axios.get(`${API}/cars`);
        setCars(response.data);
        setFilteredCars(response.data);
      } catch (error) {
        console.error("Error fetching cars:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  useEffect(() => {
    let result = [...cars];

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(car => 
        car.marque.toLowerCase().includes(query) ||
        car.modele.toLowerCase().includes(query)
      );
    }

    // Marque filter
    if (marqueFilter !== "all") {
      result = result.filter(car => car.marque === marqueFilter);
    }

    // Carburant filter
    if (carburantFilter !== "all") {
      result = result.filter(car => car.carburant === carburantFilter);
    }

    // Price range
    if (priceRange !== "all") {
      const [min, max] = priceRange.split("-").map(Number);
      result = result.filter(car => car.prix >= min && car.prix <= max);
    }

    // Availability
    if (showOnlyAvailable) {
      result = result.filter(car => car.disponible);
    }

    setFilteredCars(result);
  }, [cars, searchQuery, marqueFilter, carburantFilter, priceRange, showOnlyAvailable]);

  const marques = [...new Set(cars.map(car => car.marque))];
  const carburants = [...new Set(cars.map(car => car.carburant))];

  const clearFilters = () => {
    setSearchQuery("");
    setMarqueFilter("all");
    setCarburantFilter("all");
    setPriceRange("all");
    setShowOnlyAvailable(true);
  };

  return (
    <div data-testid="cars-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50">
        <div className="page-container">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            NOS <span className="text-yellow-500">VOITURES</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Découvrez notre sélection de véhicules d'occasion contrôlés et garantis.
          </p>
        </div>
      </section>

      {/* Filters & Listing */}
      <section className="section-spacing bg-white">
        <div className="page-container">
          {/* Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Rechercher une voiture..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 bg-white border-gray-300 h-12"
                data-testid="search-input"
              />
            </div>

            {/* Filter Toggle (Mobile) */}
            <Button
              variant="outline"
              className="lg:hidden border-gray-300"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filtres
            </Button>

            {/* Desktop Filters */}
            <div className={`flex flex-col lg:flex-row gap-4 ${showFilters ? 'flex' : 'hidden lg:flex'}`}>
              <Select value={marqueFilter} onValueChange={setMarqueFilter}>
                <SelectTrigger className="w-full lg:w-40 bg-white border-gray-300" data-testid="marque-filter">
                  <SelectValue placeholder="Marque" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes marques</SelectItem>
                  {marques.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={carburantFilter} onValueChange={setCarburantFilter}>
                <SelectTrigger className="w-full lg:w-40 bg-white border-gray-300" data-testid="carburant-filter">
                  <SelectValue placeholder="Carburant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous carburants</SelectItem>
                  {carburants.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className="w-full lg:w-48 bg-white border-gray-300" data-testid="price-filter">
                  <SelectValue placeholder="Budget" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les prix</SelectItem>
                  <SelectItem value="0-40000">Moins de 40 000 €</SelectItem>
                  <SelectItem value="40000-70000">40 000 € - 70 000 €</SelectItem>
                  <SelectItem value="70000-100000">70 000 € - 100 000 €</SelectItem>
                  <SelectItem value="100000-500000">Plus de 100 000 €</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-2" />
                Réinitialiser
              </Button>
            </div>
          </div>

          {/* Results count */}
          <div className="mb-6 text-gray-600">
            {filteredCars.length} véhicule{filteredCars.length > 1 ? 's' : ''} trouvé{filteredCars.length > 1 ? 's' : ''}
          </div>

          {/* Cars Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : filteredCars.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">Aucun véhicule ne correspond à vos critères.</p>
              <Button
                variant="link"
                onClick={clearFilters}
                className="text-yellow-600 mt-4"
              >
                Réinitialiser les filtres
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="cars-grid">
              {filteredCars.map((car, index) => (
                <div 
                  key={car.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CarCard car={car} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Cars;
